import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crown, Medal, Award, User } from "lucide-react";

interface LeaderboardUser {
  id: string;
  username: string;
  displayName?: string;
  totalEarned: number;
  avatarUrl?: string;
}

interface LeaderboardPodiumProps {
  topUsers: LeaderboardUser[];
}

const LeaderboardPodium = ({ topUsers }: LeaderboardPodiumProps) => {
  const getPodiumIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Crown className="w-8 h-8 text-yellow-400" />;
      case 2:
        return <Medal className="w-7 h-7 text-gray-300" />;
      case 3:
        return <Award className="w-7 h-7 text-amber-500" />;
      default:
        return null;
    }
  };

  const getPodiumColors = (position: number) => {
    switch (position) {
      case 1:
        return {
          cardBg: "bg-yellow-500/10",
          border: "border-yellow-500/20",
          glow: "glow-effect shadow-yellow-500/20",
          badge: "bg-yellow-500/20 border-yellow-500/20 text-yellow-500",
          textColor: "text-yellow-400",
        };
      case 2:
        return {
          cardBg: "bg-gray-500/10",
          border: "border-gray-500/20",
          glow: "",
          badge: "bg-gray-500/20 border-gray-500/20 text-gray-400",
          textColor: "text-gray-300",
        };
      case 3:
        return {
          cardBg: "bg-amber-600/10",
          border: "border-amber-600/20",
          glow: "",
          badge: "bg-amber-600/20 border-amber-600/20 text-amber-600",
          textColor: "text-amber-500",
        };
      default:
        return {
          cardBg: "bg-muted/10",
          border: "border-border",
          glow: "",
          badge: "bg-muted border-border text-muted-foreground",
          textColor: "text-muted-foreground",
        };
    }
  };

  const getAnimationDelay = (position: number) => {
    switch (position) {
      case 1:
        return 0;
      case 2:
        return 0.2;
      case 3:
        return 0.4;
      default:
        return 0.6;
    }
  };

  const orderedUsers = [
    topUsers[1], // 2nd place
    topUsers[0], // 1st place
    topUsers[2], // 3rd place
  ].filter(Boolean);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 items-end justify-center mb-12">
      {orderedUsers.map((user) => {
        const originalIndex = topUsers.findIndex(u => u.id === user.id);
        const actualPosition = originalIndex + 1;
        const colors = getPodiumColors(actualPosition);
        const heightClass = actualPosition === 1 ? "h-80" : "h-72";
        const orderClass = actualPosition === 1 ? "md:order-2" : (actualPosition === 2 ? "md:order-1" : "md:order-3");

        return (
          <motion.div
            key={user.id}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: getAnimationDelay(actualPosition),
              duration: 0.6,
              ease: "easeOut",
            }}
            className={orderClass}
          >
            <Card
              className={`glass-effect ${colors.cardBg} ${colors.border} ${colors.glow} ${heightClass} w-full transition-all duration-300 flex flex-col items-center justify-between p-6 text-center`}
              data-testid={`podium-position-${actualPosition}`}
            >
              {/* Top Icon */}
              <motion.div whileHover={{ scale: 1.15, rotate: -10 }}>
                {getPodiumIcon(actualPosition)}
              </motion.div>

              {/* User Info Block */}
              <div className="flex flex-col items-center">
                <motion.div
                  className="w-20 h-20 rounded-full bg-background/20 mb-3 flex-shrink-0 flex items-center justify-center border-2 border-border/50"
                  whileHover={{ scale: 1.1 }}
                >
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.username}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-10 h-10 text-muted-foreground" />
                  )}
                </motion.div>

                <div className="font-semibold mb-1 w-full break-words" data-testid={`podium-username-${actualPosition}`}>
                  {user.displayName || user.username}
                </div>

                <div
                  className={`text-2xl font-bold ${colors.textColor} mb-1`}
                  data-testid={`podium-earnings-${actualPosition}`}
                >
                  {user.totalEarned.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">tokens earned</div>
              </div>

              {/* Bottom Badge */}
              <div>
                {actualPosition === 1 ? (
                  <Badge className={`${colors.badge}`} data-testid="champion-badge">
                    <Crown className="w-3 h-3 mr-1" />
                    Champion
                  </Badge>
                ) : (
                  <Badge variant="outline" className={`${colors.badge}`} data-testid={`position-badge-${actualPosition}`}>
                    Rank #{actualPosition}
                  </Badge>
                )}
              </div>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
};

export default LeaderboardPodium;
